import React, { useMemo, useState, useCallback, useRef } from "react";
import { useAtelierStore, JOB_STATUSES } from "@store/atelierStore";
import { WorkshopJob, WorkshopJobStatus } from "../../types/garage/atelier";
import { AvailabilityBlock } from "@store/disponibiliteStore";
import { getMonday, toISO } from "@store/disponibiliteStore";
import { formatTime } from "@utils/timeGrid";
import {
  GridPreview,
  HOURS_START,
  HOURS_END,
  SNAP_PX,
  SNAP_HOUR,
  HOUR_BLOCK_HEIGHT,
  LUNCH_HOUR,
} from "./gridTypes";
import OrdreModal from "@components/modals/OrdreModal";
import { findOrdreForPlate, buildOrdreFromAppointment } from "@store/ordreStore";
import { ordresApi } from "@services/api/ordres";
import type { Ordre } from "types/ordre";

const CLICK_THRESHOLD = 5;

const DAY_LABELS = ["Lun", "Mar", "Mer", "Jeu", "Ven"];

function getStatusConfig(status: WorkshopJobStatus) {
  return JOB_STATUSES.find((s) => s.id === status)!;
}

function dayOfWeekIndex(dateStr: string): number {
  const d = new Date(dateStr + "T00:00:00");
  const jsDay = d.getDay();
  return jsDay === 0 ? 6 : jsDay - 1;
}

function isBlockedByDispo(
  blocks: AvailabilityBlock[],
  workerId: string,
  date: string,
  hour: number,
): boolean {
  const dow = dayOfWeekIndex(date);
  for (const b of blocks) {
    if (b.workerId !== workerId) continue;
    const matches = b.date === date || (b.recurring && b.dayOfWeek === dow);
    if (matches) {
      const blockEnd = b.startHour + b.durationHours;
      if (hour >= b.startHour && hour < blockEnd) return true;
    }
  }
  return false;
}

export function WeekTimeGridAtelier({
  blocks = [],
  workerId,
  gridScrollRef,
  gridPreview,
  setGridPreview,
  jobDragRef,
}: {
  blocks?: AvailabilityBlock[];
  workerId?: string;
  gridScrollRef?: React.RefObject<HTMLDivElement | null>;
  gridPreview?: GridPreview | null;
  setGridPreview?: (p: GridPreview | null) => void;
  jobDragRef?: React.MutableRefObject<{
    jobId: string;
    mode: "move" | "resize";
    startY: number;
    origStartHour: number;
    origDuration: number;
    origWorkerId: string;
  } | null>;
}) {
  const selectedDate = useAtelierStore((s) => s.selectedDate);
  const activeSpecialty = useAtelierStore((s) => s.activeSpecialty);
  const allWorkers = useAtelierStore((s) => s.workers);
  const allJobs = useAtelierStore((s) => s.jobs);
  const searchQuery = useAtelierStore((s) => s.searchQuery);
  const hoveredChargeId = useAtelierStore((s) => s.hoveredChargeId);
  const charges = useAtelierStore((s) => s.charges);
  const storeCarWindows = useAtelierStore((s) => s.carWindows);

  // Use persistent carWindows from store for conflict detection (survives charge deletion)
  const carWindowByOrdre = storeCarWindows;

  const isJobOutsideCarWindow = useCallback((job: WorkshopJob): boolean => {
    if (!job.ordre_id) return false;
    const cw = carWindowByOrdre[job.ordre_id];
    if (!cw || !cw.depositDate) return false;
    const restDate = cw.restitutionDate || cw.depositDate;
    if (job.date < cw.depositDate || job.date > restDate) return true;
    const jobEnd = job.startHour + job.durationHours;
    if (job.date === cw.depositDate && cw.depositStartHour != null && job.startHour < cw.depositStartHour) return true;
    if (job.date === restDate && cw.restitutionStartHour != null && jobEnd > cw.restitutionStartHour) return true;
    return false;
  }, [carWindowByOrdre]);

  const [selectedOrdre, setSelectedOrdre] = useState<Ordre | null>(null);
  const clickStartRef = useRef<{ x: number; y: number } | null>(null);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent, job: WorkshopJob, mode: "move" | "resize") => {
      if (!jobDragRef || !setGridPreview) return;
      e.preventDefault();
      e.stopPropagation();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      clickStartRef.current = { x: e.clientX, y: e.clientY };
      jobDragRef.current = {
        jobId: job.id,
        mode,
        startY: e.clientY,
        origStartHour: job.startHour,
        origDuration: job.durationHours,
        origWorkerId: job.workerId,
      };
      setGridPreview({
        type: "job",
        jobId: job.id,
        startHour: job.startHour,
        durationHours: job.durationHours,
        workerId: job.workerId,
        date: job.date,
      });
    },
    [setGridPreview, jobDragRef],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent, job: WorkshopJob) => {
      const start = clickStartRef.current;
      if (
        start &&
        Math.abs(e.clientX - start.x) < CLICK_THRESHOLD &&
        Math.abs(e.clientY - start.y) < CLICK_THRESHOLD
      ) {
        if (job.ordre_id) {
          ordresApi.get(job.ordre_id).then((ordre) => setSelectedOrdre(ordre)).catch(() => {
            setSelectedOrdre(findOrdreForPlate(job.plate) || buildOrdreFromAppointment(job));
          });
        } else {
          setSelectedOrdre(findOrdreForPlate(job.plate) || buildOrdreFromAppointment(job));
        }
      }
      clickStartRef.current = null;
    },
    [],
  );

  const worker = allWorkers.find((w) => w.id === workerId) || allWorkers[0];

  const monday = useMemo(() => getMonday(selectedDate), [selectedDate]);
  const weekDates = useMemo(
    () => Array.from({ length: 5 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return toISO(d);
    }),
    [monday],
  );

  const hours = Array.from(
    { length: HOURS_END - HOURS_START },
    (_, i) => HOURS_START + i,
  );

  // Jobs for this worker this week
  const jobsByDay = useMemo(() => {
    if (!worker) return {};
    const map: Record<string, WorkshopJob[]> = {};
    for (const date of weekDates) map[date] = [];
    for (const j of allJobs) {
      if (j.workerId !== worker.id) continue;
      if (!weekDates.includes(j.date)) continue;
      if (activeSpecialty !== "all" && j.specialty !== activeSpecialty) continue;
      if (
        searchQuery &&
        !j.clientName.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !j.plate.toLowerCase().includes(searchQuery.toLowerCase())
      )
        continue;
      if (map[j.date]) map[j.date].push(j);
    }
    return map;
  }, [worker, allJobs, weekDates, activeSpecialty, searchQuery]);

  // Resolve carWindow: from hovered charge OR from the job being dragged
  const activeCarWindow = useMemo((): import("../../types/garage/atelier").CarWindow | null => {
    if (hoveredChargeId) {
      const charge = charges.find((c) => c.id === hoveredChargeId);
      return charge?.carWindow || null;
    }
    if (gridPreview?.type === "job") {
      const job = allJobs.find((j) => j.id === gridPreview.jobId);
      if (job?.carWindow) return job.carWindow;
      if (job?.ordre_id) {
        const charge = charges.find((c) => c.ordre_id === job.ordre_id && c.carWindow);
        return charge?.carWindow || storeCarWindows[job.ordre_id] || null;
      }
    }
    return null;
  }, [hoveredChargeId, charges, gridPreview, allJobs, storeCarWindows]);

  // Compute car availability overlay per day
  const carOverlayByDay = useMemo(() => {
    if (!activeCarWindow) return {};
    const cw = activeCarWindow;
    if (!cw.depositDate) return {};
    const restDate = cw.restitutionDate || cw.depositDate;
    const result: Record<string, { startHour: number; endHour: number }> = {};
    for (const date of weekDates) {
      if (date < cw.depositDate || date > restDate) continue;
      const startHour = date === cw.depositDate && cw.depositStartHour != null
        ? cw.depositStartHour : HOURS_START;
      const endHour = date === restDate && cw.restitutionStartHour != null
        ? cw.restitutionStartHour : HOURS_END;
      result[date] = { startHour, endHour };
    }
    return result;
  }, [activeCarWindow, weekDates]);

  const headerHeight = 28;
  const hourLabelWidth = 32;
  const minuteColWidth = 24;

  if (!worker) {
    return (
      <div className="flex items-center justify-center p-10 text-neutral-400" style={{ fontFamily: "Poppins" }}>
        Aucun mécanicien trouvé
      </div>
    );
  }

  return (
    <div
      className="relative"
      style={{
        border: "1px solid #D9D9D9",
        borderRadius: 10,
        overflow: "hidden",
      }}
    >
      {/* Column headers — days of the week */}
      <div className="flex flex-row" style={{ height: headerHeight }}>
        <div
          style={{
            width: hourLabelWidth + minuteColWidth + 1,
            borderRight: "1px solid #D9D9D9",
            borderBottom: "1px solid #D9D9D9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "Poppins",
            fontSize: 11,
            color: "#A1A1A1",
            fontWeight: 500,
          }}
        >
          {worker.name}
        </div>
        {weekDates.map((date, i) => {
          const day = new Date(date + "T00:00:00").getDate();
          const isToday = date === toISO(new Date());
          return (
            <div
              key={date}
              className="flex-1 flex items-center justify-center text-sm font-medium whitespace-nowrap"
              style={{
                borderRight: "1px solid #D9D9D9",
                borderBottom: "1px solid #D9D9D9",
                background: isToday ? "#EEF2FF" : "#FFF",
                color: isToday ? "#6366F1" : "#525252",
                fontFamily: "Poppins",
              }}
            >
              {DAY_LABELS[i]} {day}
            </div>
          );
        })}
      </div>

      {/* Grid body */}
      <div className="relative" ref={gridScrollRef}>
        {/* Car availability overlays per day */}
        {activeCarWindow && Object.keys(carOverlayByDay).length > 0 && (() => {
          const totalHeight = (HOURS_END - HOURS_START) * HOUR_BLOCK_HEIGHT;
          const hourLabelW = hourLabelWidth + minuteColWidth + 1;
          // Each day column is flex-1, so we calculate proportionally
          const dayCount = weekDates.length;
          return weekDates.map((date, i) => {
            const overlay = carOverlayByDay[date];
            const colLeft = `calc(${hourLabelW}px + ${(i / dayCount) * 100}% * (1 - ${hourLabelW}px / 100%))`;
            const colWidth = `calc((100% - ${hourLabelW}px) / ${dayCount})`;
            if (!overlay) {
              // Day is outside car window — dim entire column
              return (
                <div
                  key={`dim-${date}`}
                  className="absolute pointer-events-none"
                  style={{
                    top: 0,
                    left: `calc(${hourLabelW}px + (100% - ${hourLabelW}px) * ${i / dayCount})`,
                    width: `calc((100% - ${hourLabelW}px) / ${dayCount})`,
                    height: totalHeight,
                    background: "rgba(0,0,0,0.06)",
                    zIndex: 2,
                  }}
                />
              );
            }
            const topPx = (overlay.startHour - HOURS_START) * HOUR_BLOCK_HEIGHT;
            const bottomPx = (overlay.endHour - HOURS_START) * HOUR_BLOCK_HEIGHT;
            const left = `calc(${hourLabelW}px + (100% - ${hourLabelW}px) * ${i / dayCount})`;
            const width = `calc((100% - ${hourLabelW}px) / ${dayCount})`;
            return (
              <React.Fragment key={`overlay-${date}`}>
                {topPx > 0 && (
                  <div className="absolute pointer-events-none" style={{ top: 0, left, width, height: topPx, background: "rgba(0,0,0,0.06)", zIndex: 2 }} />
                )}
                <div className="absolute pointer-events-none" style={{
                  top: topPx, left, width,
                  height: Math.max(bottomPx - topPx, 0),
                  background: "rgba(45, 212, 191, 0.08)",
                  borderTop: "2px solid rgba(45, 212, 191, 0.4)",
                  borderBottom: "2px solid rgba(45, 212, 191, 0.4)",
                  zIndex: 2,
                }} />
                {bottomPx < totalHeight && (
                  <div className="absolute pointer-events-none" style={{ top: bottomPx, left, width, height: totalHeight - bottomPx, background: "rgba(0,0,0,0.06)", zIndex: 2 }} />
                )}
              </React.Fragment>
            );
          });
        })()}
        <div className="flex flex-col">
          {hours.map((h) => {
            const isLunch = h === LUNCH_HOUR;
            return (
              <div
                key={h}
                className="flex flex-row"
                style={{
                  height: HOUR_BLOCK_HEIGHT,
                  borderBottom: "1px solid #D9D9D9",
                }}
              >
                {/* Hour label */}
                <div
                  className="flex items-start text-h4 font-medium"
                  style={{
                    width: hourLabelWidth,
                    paddingLeft: 7,
                    paddingTop: 2,
                    fontFamily: "Poppins",
                    color: "#A1A1A1",
                    flexShrink: 0,
                  }}
                >
                  {h}h
                </div>
                {/* Minute column */}
                <div
                  className="flex flex-col"
                  style={{
                    width: minuteColWidth,
                    borderLeft: "1px solid #D9D9D9",
                    borderRight: "1px solid #D9D9D9",
                    flexShrink: 0,
                  }}
                >
                  {["00", "15", "30", "45"].map((m) => (
                    <div
                      key={m}
                      className="flex items-center justify-center text-h4 font-medium"
                      style={{
                        height: 32,
                        borderBottom: "1px solid #D9D9D9",
                        fontFamily: "Poppins",
                        color: "#A1A1A1",
                      }}
                    >
                      {m}
                    </div>
                  ))}
                </div>

                {/* Day columns */}
                {weekDates.map((date) => {
                  const isBlocked = isLunch || isBlockedByDispo(blocks, worker.id, date, h);
                  const dayJobs = (jobsByDay[date] || []).filter(
                    (j) => j.startHour >= h && j.startHour < h + 1,
                  );
                  const chargePreview =
                    gridPreview?.type === "charge" &&
                    gridPreview.workerId === worker.id &&
                    gridPreview.date === date &&
                    gridPreview.startHour >= h &&
                    gridPreview.startHour < h + 1
                      ? gridPreview
                      : null;
                  const jobPreview =
                    gridPreview?.type === "job" &&
                    gridPreview.workerId === worker.id &&
                    (!gridPreview.date || gridPreview.date === date) &&
                    gridPreview.startHour >= h &&
                    gridPreview.startHour < h + 1
                      ? gridPreview
                      : null;

                  return (
                    <div
                      key={date}
                      className="flex-1 relative"
                      style={{ borderRight: "1px solid #D9D9D9" }}
                      data-worker-col={worker.id}
                      data-day-date={date}
                    >
                      {[0, 1, 2, 3].map((q) => (
                        <div key={q} style={{ height: 32, display: "flex", flexDirection: "column" }}>
                          <div
                            style={{
                              height: 16,
                              borderBottom: "1px solid #D9D9D9",
                              background: isBlocked ? "#E6E6E6" : undefined,
                            }}
                          />
                          <div
                            style={{
                              height: 16,
                              background: isBlocked ? "#E6E6E6" : "#F4F4F4",
                              borderBottom: "1px solid #D9D9D9",
                            }}
                          />
                        </div>
                      ))}

                      {/* Jobs */}
                      {dayJobs.map((job) => {
                        const config = getStatusConfig(job.status);
                        const isDragSource = gridPreview?.type === "job" && gridPreview.jobId === job.id;
                        const outsideWindow = isJobOutsideCarWindow(job);
                        const topPx = ((job.startHour - h) / SNAP_HOUR) * SNAP_PX;
                        const heightPx = (job.durationHours / SNAP_HOUR) * SNAP_PX;
                        return (
                          <div
                            key={job.id}
                            className="absolute left-0 right-0 flex flex-col cursor-grab"
                            style={{
                              top: topPx,
                              height: Math.max(heightPx, 20),
                              background: outsideWindow ? "#FEE2E2" : config.bgColor,
                              border: `1px solid ${outsideWindow ? "#FCA5A5" : config.borderColor}`,
                              boxShadow: "0px 2px 4px rgba(0,0,0,0.15)",
                              borderRadius: 5,
                              padding: "3px 4px",
                              overflow: "hidden",
                              zIndex: 10,
                              margin: "0 2px",
                              opacity: isDragSource ? 0.35 : 1,
                              touchAction: "none",
                            }}
                            onPointerDown={(e) => handlePointerDown(e, job, "move")}
                            onPointerUp={(e) => handlePointerUp(e, job)}
                            title={outsideWindow ? "Véhicule absent à cet horaire" : undefined}
                          >
                            <span
                              className="text-h4 font-medium"
                              style={{
                                lineHeight: "140%",
                                color: outsideWindow ? "#DC2626" : config.color,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {job.clientName}
                              {job.plate ? ` ${job.plate}` : ""}
                            </span>
                            {heightPx > 30 && (
                              <span
                                className="text-h5"
                                style={{ color: config.color, opacity: 0.7 }}
                              >
                                {formatTime(job.startHour)} – {formatTime(job.startHour + job.durationHours)}
                              </span>
                            )}
                            {/* Resize handle */}
                            <div
                              className="absolute bottom-0 left-0 right-0 flex justify-center items-center"
                              style={{ height: 8, cursor: "ns-resize" }}
                              onPointerDown={(e) => {
                                e.stopPropagation();
                                handlePointerDown(e, job, "resize");
                              }}
                            >
                              <div
                                style={{
                                  width: 20,
                                  height: 3,
                                  borderRadius: 2,
                                  background: config.borderColor,
                                  opacity: 0.5,
                                }}
                              />
                            </div>
                          </div>
                        );
                      })}

                      {/* Charge drop preview */}
                      {chargePreview && (() => {
                        const topPx = ((chargePreview.startHour - h) / SNAP_HOUR) * SNAP_PX;
                        const heightPx = (chargePreview.durationHours / SNAP_HOUR) * SNAP_PX;
                        // Check if preview is outside car window
                        let previewOutside = false;
                        if (activeCarWindow?.depositDate) {
                          const depDate = activeCarWindow.depositDate;
                          const restDate = activeCarWindow.restitutionDate || depDate;
                          const previewEnd = chargePreview.startHour + chargePreview.durationHours;
                          if (date < depDate || date > restDate) previewOutside = true;
                          else if (date === depDate && activeCarWindow.depositStartHour != null && chargePreview.startHour < activeCarWindow.depositStartHour) previewOutside = true;
                          else if (date === restDate && activeCarWindow.restitutionStartHour != null && previewEnd > activeCarWindow.restitutionStartHour) previewOutside = true;
                        }
                        const previewColor = previewOutside ? "#DC2626" : "#6366F1";
                        const previewBg = previewOutside ? "rgba(220, 38, 38, 0.15)" : "rgba(99, 102, 241, 0.15)";
                        return (
                          <div
                            className="absolute left-0 right-0"
                            style={{
                              top: topPx,
                              height: Math.max(heightPx, 20),
                              background: previewBg,
                              border: `2px dashed ${previewColor}`,
                              borderRadius: 5,
                              padding: "3px 4px",
                              zIndex: 20,
                              margin: "0 2px",
                              pointerEvents: "none",
                            }}
                          >
                            <span
                              className="text-h4 font-medium"
                              style={{
                                color: previewColor,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {previewOutside && "⚠ "}{chargePreview.clientName}
                            </span>
                          </div>
                        );
                      })()}

                      {/* Job drag preview */}
                      {jobPreview && (() => {
                        const previewJob = allJobs.find((j) => j.id === jobPreview.jobId);
                        if (!previewJob) return null;
                        const config = getStatusConfig(previewJob.status);
                        const topPx = ((jobPreview.startHour - h) / SNAP_HOUR) * SNAP_PX;
                        const heightPx = (jobPreview.durationHours / SNAP_HOUR) * SNAP_PX;
                        // Check if preview is outside car window
                        let previewOutside = false;
                        if (activeCarWindow?.depositDate) {
                          const depDate = activeCarWindow.depositDate;
                          const restDate = activeCarWindow.restitutionDate || depDate;
                          const previewEnd = jobPreview.startHour + jobPreview.durationHours;
                          if (date < depDate || date > restDate) previewOutside = true;
                          else if (date === depDate && activeCarWindow.depositStartHour != null && jobPreview.startHour < activeCarWindow.depositStartHour) previewOutside = true;
                          else if (date === restDate && activeCarWindow.restitutionStartHour != null && previewEnd > activeCarWindow.restitutionStartHour) previewOutside = true;
                        }
                        const pColor = previewOutside ? "#DC2626" : config.color;
                        const pBg = previewOutside ? "#FEE2E2" : config.bgColor;
                        const pBorder = previewOutside ? "#FCA5A5" : config.borderColor;
                        return (
                          <div
                            className="absolute left-0 right-0"
                            style={{
                              top: topPx,
                              height: Math.max(heightPx, 20),
                              background: pBg,
                              border: `2px dashed ${pBorder}`,
                              borderRadius: 5,
                              padding: "3px 4px",
                              zIndex: 20,
                              margin: "0 2px",
                              pointerEvents: "none",
                              opacity: 0.7,
                            }}
                          >
                            <span
                              className="text-h4 font-medium"
                              style={{
                                color: pColor,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {previewOutside && "⚠ "}{previewJob.clientName}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {selectedOrdre && (
        <OrdreModal
          ordre={selectedOrdre}
          onClose={() => setSelectedOrdre(null)}
          onSave={() => setSelectedOrdre(null)}
        />
      )}
    </div>
  );
}
